import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { GOTY_CATEGORIES, ranksForCategory, PERFORMANCE_CATEGORIES, CHARACTER_CATEGORIES } from '../utils/gotyCategories';
import GameSearchPicker from './GameSearchPicker';
import Modal from './Modal';
import { useAuth } from '../AuthContext';

const entryKey = (category, rank) => `${category}:${rank}`;
const emptyEntry = () => ({ title: '', posterFile: null, posterPreview: null, existingPosterUrl: null });

// A year's worth of GOTY entries is a set of goty_awards rows (one per category+rank). Everything
// is freeform - title and poster are typed/uploaded directly, there's no link to `games` or
// `todo_games`. The search box next to each field is just a shortcut: picking a game there
// autofills the title/poster from it, nothing is stored as a reference.
function GOTYSection() {
  const { user } = useAuth();
  const [pickableGames, setPickableGames] = useState([]);
  const [awards, setAwards] = useState([]);
  const [showManage, setShowManage] = useState(false);
  const [yearDraft, setYearDraft] = useState('');
  const [yearLocked, setYearLocked] = useState(false);
  const [entries, setEntries] = useState({});
  const [performanceDetails, setPerformanceDetails] = useState({});
  const [characterNames, setCharacterNames] = useState({});
  const [momentDescription, setMomentDescription] = useState('');
  // Display order of the Top 5 slots in the form: gotyOrder[position] is the rank whose entry data
  // currently sits at that position. Dragging only reshuffles this array; the entries themselves
  // stay keyed by their original rank until Save writes them out under their new rank.
  const [gotyOrder, setGotyOrder] = useState([1, 2, 3, 4, 5]);
  const [draggedPos, setDraggedPos] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [loadError, setLoadError] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);

  const fetchAwards = useCallback(async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/goty_awards?select=*&order=year.desc`, {
        headers: supabaseHeaders(false),
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`${res.status}: ${detail}`);
      }
      setAwards(await res.json());
      setLoadError('');
    } catch (err) {
      console.error('Failed to fetch GOTY awards:', err);
      setLoadError(`Couldn't load GOTY awards (${err.message}). Have you run the goty_awards migration in Supabase yet?`);
    }
  }, []);

  const fetchPickableGames = useCallback(async () => {
    try {
      const [gamesRes, todoRes] = await Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/games?select=id,title,poster_url&order=title.asc&limit=1000`, {
          headers: supabaseHeaders(false),
        }),
        fetch(`${SUPABASE_URL}/rest/v1/todo_games?select=id,title,poster_url&order=title.asc&limit=1000`, {
          headers: supabaseHeaders(false),
        }),
      ]);
      if (!gamesRes.ok) throw new Error('Failed to fetch games');
      if (!todoRes.ok) throw new Error('Failed to fetch to-do games');
      const [gamesData, todoData] = await Promise.all([gamesRes.json(), todoRes.json()]);
      setPickableGames([
        ...gamesData.map((g) => ({ ...g, name: g.title, source: 'games' })),
        ...todoData.map((g) => ({ ...g, name: g.title, title: `${g.title} (to-do)`, source: 'todo_games' })),
      ]);
    } catch (err) {
      console.error('Failed to fetch pickable games:', err);
    }
  }, []);

  useEffect(() => {
    fetchAwards();
    fetchPickableGames();
  }, [fetchAwards, fetchPickableGames]);

  const years = useMemo(
    () => [...new Set(awards.map((a) => a.year))].sort((a, b) => b - a),
    [awards]
  );
  const awardFor = (year, category, rank = 1) =>
    awards.find((a) => a.year === year && a.category === category && a.rank === rank);

  // Default to the highest year, and follow along if it disappears (e.g. its awards got deleted).
  useEffect(() => {
    if (years.length === 0) return;
    if (selectedYear === null || !years.includes(selectedYear)) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const openAddYear = () => {
    const suggested = years.length ? years[0] + 1 : new Date().getFullYear();
    const initial = {};
    GOTY_CATEGORIES.forEach((cat) => {
      ranksForCategory(cat.value).forEach((rank) => {
        initial[entryKey(cat.value, rank)] = emptyEntry();
      });
    });
    const initialPerformance = {};
    PERFORMANCE_CATEGORIES.forEach((catValue) => {
      initialPerformance[catValue] = { actor: '', character: '' };
    });
    const initialCharacterNames = {};
    CHARACTER_CATEGORIES.forEach((catValue) => {
      initialCharacterNames[catValue] = '';
    });
    setYearDraft(String(suggested));
    setYearLocked(false);
    setEntries(initial);
    setPerformanceDetails(initialPerformance);
    setCharacterNames(initialCharacterNames);
    setMomentDescription('');
    setGotyOrder([1, 2, 3, 4, 5]);
    setStatus('');
    setShowManage(true);
  };

  const openEditYear = (year) => {
    const initial = {};
    GOTY_CATEGORIES.forEach((cat) => {
      ranksForCategory(cat.value).forEach((rank) => {
        const existing = awardFor(year, cat.value, rank);
        initial[entryKey(cat.value, rank)] = {
          title: existing?.title || '',
          posterFile: null,
          posterPreview: null,
          existingPosterUrl: existing?.poster_url || null,
        };
      });
    });
    const initialPerformance = {};
    PERFORMANCE_CATEGORIES.forEach((catValue) => {
      const existing = awardFor(year, catValue, 1);
      initialPerformance[catValue] = {
        actor: existing?.actor || '',
        character: existing?.character_name || '',
      };
    });
    const initialCharacterNames = {};
    CHARACTER_CATEGORIES.forEach((catValue) => {
      const existing = awardFor(year, catValue, 1);
      initialCharacterNames[catValue] = existing?.character_name || '';
    });
    const momentAward = awardFor(year, 'favorite_moment', 1);
    setYearDraft(String(year));
    setYearLocked(true);
    setEntries(initial);
    setPerformanceDetails(initialPerformance);
    setCharacterNames(initialCharacterNames);
    setMomentDescription(momentAward?.description || '');
    setGotyOrder([1, 2, 3, 4, 5]);
    setStatus('');
    setShowManage(true);
  };

  const handleGotyDrop = (pos) => {
    if (draggedPos === null || draggedPos === pos) { setDraggedPos(null); return; }
    setGotyOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggedPos, 1);
      next.splice(pos, 0, moved);
      return next;
    });
    setDraggedPos(null);
  };

  // Which entry-rank maps to which final rank for a category, accounting for the Top 5 drag order.
  const rankSourceMap = (cat) => {
    if (cat.value === 'game_of_the_year') {
      return gotyOrder.map((sourceRank, idx) => ({ finalRank: idx + 1, sourceRank }));
    }
    return ranksForCategory(cat.value).map((rank) => ({ finalRank: rank, sourceRank: rank }));
  };

  const handleSuggestionPick = (key, game) => {
    setEntries((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        title: game.name,
        posterFile: null,
        posterPreview: null,
        existingPosterUrl: game.poster_url || prev[key].existingPosterUrl,
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const year = parseInt(yearDraft, 10);
    if (!year) { setStatus('Please fill in a valid year.'); return; }
    setSaving(true);
    setStatus('');
    try {
      for (const cat of GOTY_CATEGORIES) {
        const isPerformance = PERFORMANCE_CATEGORIES.includes(cat.value);
        const isCharacterOnly = CHARACTER_CATEGORIES.includes(cat.value);
        const isMoment = cat.value === 'favorite_moment';
        for (const { finalRank, sourceRank } of rankSourceMap(cat)) {
          const key = entryKey(cat.value, sourceRank);
          const entry = entries[key] || emptyEntry();
          const existing = awardFor(year, cat.value, finalRank);
          const hasTitle = entry.title.trim() !== '';

          if (existing && !hasTitle) {
            const res = await fetch(`${SUPABASE_URL}/rest/v1/goty_awards?id=eq.${existing.id}`, {
              method: 'DELETE',
              headers: supabaseHeaders(true),
            });
            if (!res.ok) throw new Error(`Failed to clear ${cat.label} (${res.status})`);
            continue;
          }

          if (!hasTitle) continue;

          let posterUrl = entry.existingPosterUrl || null;
          if (entry.posterFile) posterUrl = await uploadPoster(entry.posterFile);

          const performanceEntry = performanceDetails[cat.value] || { actor: '', character: '' };
          const actor = isPerformance ? (performanceEntry.actor.trim() || null) : null;
          const character = isPerformance
            ? (performanceEntry.character.trim() || null)
            : isCharacterOnly
              ? ((characterNames[cat.value] || '').trim() || null)
              : null;
          const description = isMoment ? (momentDescription.trim() || null) : null;
          const payload = { title: entry.title.trim(), poster_url: posterUrl, actor, character_name: character, description };

          if (!existing) {
            const res = await fetch(`${SUPABASE_URL}/rest/v1/goty_awards`, {
              method: 'POST',
              headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
              body: JSON.stringify({ year, category: cat.value, rank: finalRank, ...payload }),
            });
            if (!res.ok) {
              const detail = await res.text();
              throw new Error(`Failed to save ${cat.label} (${res.status}): ${detail}`);
            }
          } else {
            const changed = payload.title !== existing.title
              || posterUrl !== (existing.poster_url || null)
              || actor !== (existing.actor || null)
              || character !== (existing.character_name || null)
              || description !== (existing.description || null);
            if (changed) {
              const res = await fetch(`${SUPABASE_URL}/rest/v1/goty_awards?id=eq.${existing.id}`, {
                method: 'PATCH',
                headers: supabaseHeaders(true),
                body: JSON.stringify(payload),
              });
              if (!res.ok) {
                const detail = await res.text();
                throw new Error(`Failed to save ${cat.label} (${res.status}): ${detail}`);
              }
            }
          }
        }
      }
      await fetchAwards();
      setSelectedYear(year);
      setShowManage(false);
    } catch (err) {
      console.error(err);
      setStatus(`Something went wrong: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteYear = async () => {
    const year = parseInt(yearDraft, 10);
    if (!window.confirm(`Delete all ${year} GOTY awards?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/goty_awards?year=eq.${year}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchAwards();
      setShowManage(false);
    } catch (err) {
      setStatus(`Something went wrong: ${err.message}`);
    }
  };

  const regularCategories = GOTY_CATEGORIES.filter((c) => c.value !== 'game_of_the_year');
  const gotyCategory = GOTY_CATEGORIES.find((c) => c.value === 'game_of_the_year');

  return (
    <>
      <div className="header">
        <div className="header-title-row">
          <h1>Game of the Year</h1>
          {user && (
            <button
              onClick={openAddYear}
              style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', background: 'white', border: '2px solid black', borderRadius: '8px', padding: '0.2em 0.6em', cursor: 'pointer' }}
            >
              Add Year
            </button>
          )}
        </div>
      </div>

      {loadError && <p className="form-status">{loadError}</p>}

      {!loadError && years.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {selectedYear !== null && years.includes(selectedYear) && (
        <div className="goty-year-block">
          <div className="goty-year-header">
            <div className="tab-pills">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={year === selectedYear ? 'tab-pill tab-pill--active' : 'tab-pill'}
                  onClick={() => setSelectedYear(year)}
                >
                  {year}
                </button>
              ))}
            </div>
            {user && <button onClick={() => openEditYear(selectedYear)}>Edit</button>}
          </div>

          <div className="goty-top5">
            <div className="goty-top5-label">{`${gotyCategory.label} — Top 5`}</div>
            <div className="goty-top5-list">
              {ranksForCategory(gotyCategory.value).map((rank) => {
                const award = awardFor(selectedYear, gotyCategory.value, rank);
                return (
                  <div className="goty-top5-item" key={rank}>
                    <div className="goty-top5-rank">#{rank}</div>
                    {award?.poster_url && <img src={award.poster_url} alt={award.title} className="goty-top5-poster" />}
                    <div className="goty-top5-title">{award?.title || '—'}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="goty-grid">
            {regularCategories.map((cat) => {
              const award = awardFor(selectedYear, cat.value, 1);
              return (
                <div className="goty-card" key={cat.value}>
                  <div className="goty-card-category">{cat.label}</div>
                  {award?.poster_url && <img src={award.poster_url} alt={award.title} className="goty-card-poster" />}
                  <div className="goty-card-winner">{award?.title || '—'}</div>
                  {(PERFORMANCE_CATEGORIES.includes(cat.value) || CHARACTER_CATEGORIES.includes(cat.value)) && (award?.character_name || award?.actor) && (
                    <div className="goty-card-detail">
                      {award.character_name}
                      {award.character_name && award.actor ? ' — ' : ''}
                      {award.actor}
                    </div>
                  )}
                  {cat.value === 'favorite_moment' && award?.description && (
                    <div className="goty-card-detail">{award.description}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showManage && (
        <Modal onClose={() => setShowManage(false)}>
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h2 style={{ fontFamily: 'angelWish', fontSize: '2rem', letterSpacing: '0.06em', marginBottom: 0 }}>
              Game of the Year
            </h2>

            <div className="form-group">
              <label>Year *</label>
              <input
                type="number"
                value={yearDraft}
                onChange={(e) => setYearDraft(e.target.value)}
                disabled={yearLocked}
                required
              />
            </div>

            {GOTY_CATEGORIES.map((cat) => (
              <React.Fragment key={cat.value}>
                {rankSourceMap(cat).map(({ finalRank, sourceRank }, pos) => {
                  const key = entryKey(cat.value, sourceRank);
                  const entry = entries[key] || emptyEntry();
                  const isGoty = cat.value === 'game_of_the_year';
                  return (
                    <div
                      className={isGoty ? 'form-group goty-draggable' : 'form-group'}
                      key={isGoty ? `goty-pos-${pos}` : key}
                      draggable={isGoty}
                      onDragStart={isGoty ? () => setDraggedPos(pos) : undefined}
                      onDragOver={isGoty ? (e) => e.preventDefault() : undefined}
                      onDrop={isGoty ? () => handleGotyDrop(pos) : undefined}
                      onDragEnd={isGoty ? () => setDraggedPos(null) : undefined}
                      style={isGoty && draggedPos === pos ? { opacity: 0.4 } : undefined}
                    >
                      <label>{isGoty ? `${cat.label} — #${finalRank}` : cat.label}</label>
                      <GameSearchPicker
                        suggestions={pickableGames}
                        onPick={(game) => handleSuggestionPick(key, game)}
                      />
                      <input
                        type="text"
                        placeholder="Title"
                        value={entry.title}
                        onChange={(e) => setEntries((prev) => ({ ...prev, [key]: { ...prev[key], title: e.target.value } }))}
                      />
                      <div className="goty-poster-input">
                        {(entry.posterPreview || entry.existingPosterUrl) && (
                          <img src={entry.posterPreview || entry.existingPosterUrl} alt="" className="goty-poster-thumb" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files[0];
                            setEntries((prev) => ({
                              ...prev,
                              [key]: { ...prev[key], posterFile: f, posterPreview: f ? URL.createObjectURL(f) : prev[key].posterPreview },
                            }));
                          }}
                        />
                      </div>
                      {PERFORMANCE_CATEGORIES.includes(cat.value) && (
                        <div className="goty-performance-fields">
                          <input
                            type="text"
                            placeholder="Actor"
                            value={performanceDetails[cat.value]?.actor || ''}
                            onChange={(e) => setPerformanceDetails((prev) => ({
                              ...prev,
                              [cat.value]: { ...prev[cat.value], actor: e.target.value },
                            }))}
                          />
                          <input
                            type="text"
                            placeholder="Character"
                            value={performanceDetails[cat.value]?.character || ''}
                            onChange={(e) => setPerformanceDetails((prev) => ({
                              ...prev,
                              [cat.value]: { ...prev[cat.value], character: e.target.value },
                            }))}
                          />
                        </div>
                      )}
                      {CHARACTER_CATEGORIES.includes(cat.value) && (
                        <input
                          type="text"
                          placeholder="Character"
                          value={characterNames[cat.value] || ''}
                          onChange={(e) => setCharacterNames((prev) => ({ ...prev, [cat.value]: e.target.value }))}
                          style={{ marginTop: '0.5rem' }}
                        />
                      )}
                      {cat.value === 'favorite_moment' && (
                        <textarea
                          placeholder="Describe the moment..."
                          value={momentDescription}
                          onChange={(e) => setMomentDescription(e.target.value)}
                          rows={3}
                          style={{ marginTop: '0.5rem' }}
                        />
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}

            {status && <p className="form-status">{status}</p>}
            <div className="edit-modal-actions">
              <button type="button" className="form-submit btn-cancel" onClick={() => setShowManage(false)}>Cancel</button>
              {yearLocked && (
                <button type="button" className="form-submit btn-delete" onClick={handleDeleteYear}>Delete Year</button>
              )}
              <button type="submit" disabled={saving} className="form-submit">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

export default GOTYSection;
